import { useAtomValue } from 'jotai';
import { pick } from 'lodash-es';
import { FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormFactoryNoLayout } from '../../../../../components/Form/FormFactory';
import { IDealDataModel } from '../../../../../data-models/deal.data-model';
import { field2ToField3, FieldEntity } from '../../../../../data-models/field2.data-model';
import { field3ToFormField } from '../../../../../data-models/field3.data-model';
import { DealFormViewModel, useDealFormSchema } from '../../../../../schemas/deal.schema';
import {
  customFieldsByEntityAtom,
  isCustomFieldKey,
} from '../../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { FDMap } from '../../../../../util/data-structure/FDMap';
import { FMT } from '../../../../../util/formatter-service';
import { schemaToFormFields } from '../../../../../util/schema-utils';
import { createForm } from '../../../../../view-models/form.view-model';
import { UnstyledFieldset } from '../../../../CompanyProfiles/Scenarios/components/commonStyledComponents';
import { FieldsWrapper, usePanelPagination } from '../../../../CompanyProfiles2/Summary/CompanyDetailsPanel';
import { Panel } from '../../../../CompanyProfiles2/Summary/Panel';
import { useDebouncedUpdateDeal } from '../../../hooks/useDealActions';
import { useLabelCreation } from '../../../hooks/useLabelCreation';
import {
  availableDealAuditAndCustomFieldsAtom,
  DealAuditAndCustomFieldsKeyType,
} from '../../../state/DealboardPreferences';
import { PanelPreferences } from '../../../../../data-models/preferences-v3.data-model';
import { dealPanelFieldContainerCss } from './DealPanelStyles';

export interface IDealPanelProps {
  deal: IDealDataModel;
  editable?: boolean;
  preferences: PanelPreferences<DealAuditAndCustomFieldsKeyType>;
}

export const DealAuditAndCustomDataPanel: FC<IDealPanelProps> = ({ deal, preferences, editable = true }) => {
  const updateDeal = useDebouncedUpdateDeal();
  const defaultFields = useAtomValue(availableDealAuditAndCustomFieldsAtom);

  // default fields are dynamic, so should not be stored in prefs;
  // if preferences.visible and no selected fields, show all available
  const fields = preferences.selectedFields.length > 0 ? preferences.selectedFields : (defaultFields ?? []);

  const methods = useForm<Partial<DealFormViewModel>>({
    defaultValues: {
      ...(pick(deal, 'customData') ?? {}),
      createdAt: deal.createdAt as Date | null,
      createdBy: FMT.format('userByEmail', deal.createdBy),
      labels: deal.labels,
    },
  });

  const onUpdateDeal = useCallback(
    async (payload: Partial<DealFormViewModel>) => {
      const { companyId, dealTypeId } = deal;
      updateDeal(deal, { companyId, dealTypeId, ...payload } as Partial<IDealDataModel>, false);
    },
    [deal, updateDeal]
  );

  return (
    <FormProvider {...methods}>
      <DealCustomFields onChange={onUpdateDeal} editable={editable} selectedFields={fields} />
    </FormProvider>
  );
};

interface IDealCustomFieldsProps {
  onChange: (data: Partial<DealFormViewModel>) => Promise<void>;
  editable?: boolean;
  selectedFields: DealAuditAndCustomFieldsKeyType[];
  defaultPage?: number;
}
function DealCustomFields({
  onChange,
  editable = true,
  selectedFields,
  defaultPage,
}: IDealCustomFieldsProps) {
  const { processLabels } = useLabelCreation();
  const dealCustomFields = useAtomValue(customFieldsByEntityAtom).get(FieldEntity.deal);
  const dealFields = schemaToFormFields(
    useDealFormSchema().pick(
      (selectedFields?.filter((f) => !isCustomFieldKey(f)) ?? []) as readonly (keyof Omit<
        DealFormViewModel,
        'customData'
      >)[]
    )
  );

  const customFields = useMemo(() => {
    const fieldsToShowAsSet = new Set(selectedFields);
    const allCustomFields = (dealCustomFields ?? []).map((f) => ({
      ...field3ToFormField(field2ToField3(f)),
      disabled: !editable,
    }));
    if (selectedFields) {
      return allCustomFields.filter((f) => fieldsToShowAsSet.has(f.key as keyof DealFormViewModel));
    }
    return allCustomFields;
  }, [dealCustomFields, editable, selectedFields]);

  const { paginationConfig, visibleFields } = usePanelPagination({
    fields: [...dealFields, ...customFields],
    initialPage: defaultPage,
  });

  const form = createForm<DealFormViewModel>({
    fields: FDMap.fromArray(visibleFields, 'key'),
    variant: 'form-inline',
  });

  const handleChange = useCallback(
    async (data: Partial<DealFormViewModel>) => {
      if (data.labels) {
        data.labels = await processLabels(data.labels);
      }
      onChange(data);
    },
    [onChange, processLabels]
  );

  return (
    <Panel title='Audit & Custom Fields' paginationConfig={paginationConfig}>
      <UnstyledFieldset disabled={!editable} css={dealPanelFieldContainerCss}>
        <FieldsWrapper>
          <FormFactoryNoLayout form={form} onChange={handleChange} />
        </FieldsWrapper>
      </UnstyledFieldset>
    </Panel>
  );
}
